/**
 * The one place a refused credential is reported, and the reason it is a callback rather than an import.
 *
 * [decision 0245] says a `401` on any candidate-journey call returns the app to the PIN pad — *"new
 * behaviour, in one place, for every call"*, in its own words. The place is {@link kioskFetch}, which every
 * service goes through. What it cannot do is reach into the session stores itself: `kioskSession` imports
 * `handoff`, `handoff` imports `kioskFetch`, and a `kioskFetch` that imported `kioskSession` would close
 * that loop. So the store side REGISTERS what to do and this module knows nothing about it.
 *
 * ⛔ **A 503 must never come through here.** The server separates *you are not enrolled* (401) from *the
 * store did not answer* (503) deliberately, and `redisHighAvailability = 'Disabled'` on dev and qa means
 * ordinary maintenance supplies the second: treating them as one case sends every tablet in the estate to a
 * staff-facing PIN pad over a cache blip [0245; `guards/kiosk-session.guard.ts`].
 */
type UnauthenticatedHandler = () => void;

let handler: UnauthenticatedHandler | undefined;

/**
 * Registers what happens when the server refuses this device's credential.
 *
 * One handler, replaced rather than added to: two subscribers would mean two screens racing to answer one
 * refusal, and the answer is a single navigation.
 */
export function onUnauthenticated(next: UnauthenticatedHandler): void {
  handler = next;
}

/** Called by {@link kioskFetch} on a 401, and by nothing else. */
export function reportUnauthenticated(): void {
  handler?.();
}
